import * as React from "react"
import {Box, Grid, Typography, Chip, Tooltip} from "@mui/material";
import {AppLink} from "@ui-seeker/app";
import {ButtonLoadingSeeker, IconSeeker, ImageSeeker} from "@ui-seeker/core";
import {formatDate, formatPrice} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {rawMatcherImageComparatorModalData} from "../@signals";
import {useProxiedImage} from "../../@hooks";


// Función para determinar el color del badge según el score
const getScoreColor = (distance) => {
	const score = parseFloat(distance)
	if (isNaN(score)) return 'default'

	if (score >= 0.9) return 'success'      // Verde - Excelente match
	if (score >= 0.7) return 'primary'      // Azul - Buen match
	if (score >= 0.5) return 'warning'      // Naranja - Match medio
	return 'error'                          // Rojo - Match bajo
}

export default function RawMatcherTableRow({ item, onValidate, onEdit, onNotFound, isValidating }) {
	const { productID, productName, brand, retailer, country, siteID, aiCandidate, thumbnail } = item
	const hasCandidate = aiCandidate !== null
	const { decodedURL: __productThumbnail, imageResourceURL: productImageURL } = useProxiedImage(thumbnail)
	const { decodedURL: candidateThumbnail, imageResourceURL: candidateImageURL } = useProxiedImage(aiCandidate?.skuImage)

	// Construir nombre completo del candidato SKU
	const getCandidateName = () => {
		if (!aiCandidate) return 'No Candidate'
		const parts = [
			aiCandidate.skuName,
			aiCandidate.variant
		].filter(Boolean)
		return parts.join(' ')
	}
	const candidateName = getCandidateName()
	// A sku with no url on this site used to open an empty tab, so it is left as plain text
	const hasSiteURL = aiCandidate?.hasSiteURL !== false
	const candidateNameLabel = <Typography display="flex" alignItems="center" gap={0.5} fontWeight="bold" fontSize="0.95rem">
		{aiCandidate?.skuName} {aiCandidate?.variant && `${aiCandidate.variant}`}
		{hasSiteURL && <IconSeeker icon="external-link" sx={{ color: 'text.disabled', fontSize: "0.75rem" }} />}
	</Typography>

	return <Grid container
		spacing={2}
		p={2}
		mb={2}
		sx={{
			border: '2px solid',
			borderColor: hasCandidate ? 'divider' : 'warning.light',
			borderRadius: 1,
			backgroundColor: 'background.paper'
		}}
	>
		{/* Product Section - Left */}
		<Grid item xs={6}>
			<Box display="flex" flexDirection="column" gap={1}>
				<Box display="flex" gap={2} alignItems="flex-start">
					<ImageSeeker
						useSpinner
						lazyLoad={false}
						src={productImageURL}
						sx={{
							border: '1px solid',
							borderColor: 'divider',
							width: 120,
							height: 'auto',
							maxHeight: 150,
							cursor: 'pointer',
							borderRadius: 1
						}}
						onClick={() => rawMatcherImageComparatorModalData.value = {
							title: `${productName} vs ${candidateName}`,
							fullUrlLeft: __productThumbnail,
							urlRight: candidateThumbnail,
							productName: productName,
							candidateName: candidateName,
							targetIndex: 0
						}}
					/>

					<Box flex={1}>
						<Typography fontWeight="bold" fontSize="0.95rem" mb={0.5}>
							{productName}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>Product ID:</strong> {productID}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>Brand:</strong> {brand || "-"}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>Retailer:</strong> {retailer || "-"}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>Site ID:</strong> {siteID || "-"}
						</Typography>
					</Box>
				</Box>
			</Box>
		</Grid>

		{/* AI Candidate (SKU) Section - Right */}
		<Grid item xs={6}>
			{hasCandidate ? (
				<Box display="flex" flexDirection="column" gap={1} height="100%">
					{aiCandidate.alreadyMapped && (
						<Box
							display="flex"
							alignItems="center"
							gap={1}
							bgcolor="warning.light"
							borderRadius={1}
							p={1}
							sx={{ opacity: 0.9 }}
						>
							<IconSeeker icon="triangle-exclamation" sx={{ color: 'warning.dark', fontSize: "1rem" }} />
							<Typography fontWeight="bold" fontSize="0.85rem" color="warning.dark">
								Already Mapped to Product: {aiCandidate.productNameMapped || aiCandidate.idProductMapped} ({aiCandidate.idProductMapped})
							</Typography>
						</Box>
					)}

					<Box display="flex" gap={2} alignItems="flex-start" flex={1}>
						<Box position="relative">
							<ImageSeeker
								useSpinner
								lazyLoad={false}
								src={`${BACKEND_APIS.BACKEND}/sku/image/${aiCandidate.skuID}`}
								sx={{
									border: '1px solid',
									borderColor: 'divider',
									width: 120,
									height: 'auto',
									maxHeight: 150,
									cursor: 'pointer',
									borderRadius: 1
								}}
								onClick={() => rawMatcherImageComparatorModalData.value = {
									title: `${productName} vs ${candidateName}`,
									fullUrlLeft: __productThumbnail,
									urlRight: `${BACKEND_APIS.BACKEND}/sku/image/${aiCandidate.skuID}`,
									productName: productName,
									candidateName: candidateName,
									targetIndex: 0
								}}
							/>
							<Box position="absolute" top={-8} right={-8}>
								<Chip
									icon={<IconSeeker icon="sparkles" sx={{ fontSize: "0.7rem !important", color: "inherit !important" }} />}
									label={aiCandidate.AIDistance ? `${Math.round(parseFloat(aiCandidate.AIDistance) * 100)}%` : "N/A"}
									color={getScoreColor(aiCandidate.AIDistance)}
									variant="filled"
									size="small"
									sx={{ fontSize: "0.7rem", height: 20, fontWeight: 'bold' }}
								/>
							</Box>
						</Box>

						<Box flex={1}>
							<Box display="flex" alignItems="center" gap={1} mb={0.5}>
								{hasSiteURL
									? <AppLink
										to={`${BACKEND_APIS.MATCHERS}/skus/main-url/${aiCandidate.skuID}/${siteID}`}
										target="_blank"
									>
										{candidateNameLabel}
									</AppLink>
									: <Tooltip title="No URL for this retailer">{candidateNameLabel}</Tooltip>
								}
								<Chip
									label={retailer}
									size="small"
									color="info"
									sx={{ fontSize: "0.7rem", height: 20 }}
								/>
							</Box>
							<Typography fontSize="0.85rem" color="text.secondary" mt={0.5}>
								<strong>SKU ID:</strong> {aiCandidate.skuID}
							</Typography>
							<Typography fontSize="0.85rem" color="text.secondary">
								<strong>Variant:</strong> {aiCandidate.variant || "-"}
							</Typography>
							<Typography fontSize="0.85rem" color="text.secondary">
								<strong>Price:</strong> {formatPrice(aiCandidate.actualPrice, aiCandidate.currency)}
							</Typography>
							<Typography fontSize="0.85rem" color="text.secondary">
								<strong>End Date:</strong> {formatDate(aiCandidate.date, { isString: true })}
							</Typography>
						</Box>

						{/* Action Buttons */}
						<Box display="flex" flexDirection="column" gap={1} justifyContent="flex-end" minWidth={140}>
							<ButtonLoadingSeeker
								color="success"
								size="small"
								loading={isValidating}
								disabled={!hasCandidate}
								startIcon={<IconSeeker icon="badge-check" />}
								onClick={() => onValidate(item)}
								sx={{ width: '100%' }}
							>
								Validate
							</ButtonLoadingSeeker>

							<ButtonLoadingSeeker
								color="error"
								size="small"
								loading={isValidating}
								startIcon={<IconSeeker icon="person-circle-question" />}
								onClick={() => onNotFound(item)}
								sx={{ width: '100%' }}
							>
								Not Found
							</ButtonLoadingSeeker>

							<ButtonLoadingSeeker
								color="primary"
								size="small"
								startIcon={<IconSeeker icon="pen-to-square" />}
								onClick={() => onEdit(item)}
								sx={{ width: '100%' }}
							>
								Edit
							</ButtonLoadingSeeker>
						</Box>
					</Box>
				</Box>
			) : (
				<Box
					display="flex"
					flexDirection="column"
					justifyContent="center"
					alignItems="center"
					height="100%"
					gap={2}
				>
					<IconSeeker icon="circle-xmark" sx={{ fontSize: "3rem", color: 'text.disabled' }} />
					<Typography variant="h6" color="text.disabled">
						No AI Candidate Available
					</Typography>
					<Box display="flex" gap={1}>
						<ButtonLoadingSeeker
							color="error"
							size="small"
							loading={isValidating}
							startIcon={<IconSeeker icon="person-circle-question" />}
							onClick={() => onNotFound(item)}
						>
							Not Found
						</ButtonLoadingSeeker>
						<ButtonLoadingSeeker
							color="primary"
							size="small"
							startIcon={<IconSeeker icon="pen-to-square" />}
							onClick={() => onEdit(item)}
						>
							Edit
						</ButtonLoadingSeeker>
					</Box>
				</Box>
			)}
		</Grid>
	</Grid>
}
